import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import FilterBar from '../components/ui/FilterBar';

const API = `${API_V1}/tax`;

/* -- Types -- */

interface Jurisdiction {
  id: number;
  jurisdiction_code: string;
  jurisdiction_name: string;
  country: string;
  region: string | null;
  tax_type: string;
  rate: number;
  is_compound: boolean;
  is_recoverable: boolean;
  effective_from: string | null;
  effective_to: string | null;
  gl_account_payable: string | null;
  gl_account_receivable: string | null;
  is_active: boolean;
}

interface TaxTxn {
  id: number;
  entity_code: string;
  transaction_type: string;
  transaction_id: number;
  jurisdiction_code: string;
  tax_type: string;
  base_amount: number;
  tax_rate: number;
  tax_amount: number;
  is_recoverable: boolean;
  status: string;
  tax_period: string;
}

interface Dashboard {
  current_period: string;
  output_tax: number;
  input_tax: number;
  net_payable: number;
  withholding_collected: number;
  active_jurisdictions: number;
  total_transactions: number;
}

interface VATReturn {
  period: string;
  entity_code: string;
  output_vat: number;
  input_vat: number;
  recoverable_input: number;
  net_payable: number;
  net_refundable: number;
  output_count: number;
  input_count: number;
}

const TAX_TYPES = ['SALES_TAX', 'VAT', 'WITHHOLDING', 'GST'] as const;

const emptyJurForm = {
  jurisdiction_code: '',
  jurisdiction_name: '',
  country: '',
  region: '',
  tax_type: 'SALES_TAX',
  rate: '',
  is_compound: false,
  is_recoverable: true,
  gl_account_payable: '',
  gl_account_receivable: '',
  is_active: true,
};

export default function TaxManagement() {
  const [tab, setTab] = useState('dashboard');

  return (
    <PageLayout title="Tax Management" subtitle="Jurisdictions, transactions, and VAT returns">
      <TabBar
        tabs={[
          { key: 'dashboard', label: 'Dashboard' },
          { key: 'jurisdictions', label: 'Jurisdictions' },
          { key: 'transactions', label: 'Transactions' },
          { key: 'vat', label: 'VAT Return' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'dashboard' && <DashboardTab />}
      {tab === 'jurisdictions' && <JurisdictionsTab />}
      {tab === 'transactions' && <TransactionsTab />}
      {tab === 'vat' && <VATReturnTab />}
    </PageLayout>
  );
}


/* -- Dashboard Tab -- */

function DashboardTab() {
  const { entity } = useGlobalEntity();
  const [data, setData] = useState<Dashboard | null>(null);

  useEffect(() => {
    authFetch(withEntityParam(`${API}/dashboard`, entity)).then(r => r.json()).then(setData).catch(() => {});
  }, [entity]);

  if (!data) return <p style={{ color: 'var(--text-hint)' }}>Loading...</p>;

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-hint)', marginBottom: 16 }}>
        Current period: <strong>{data.current_period}</strong>
      </p>
      <KpiGrid items={[
        { label: 'Output Tax', value: formatAmountCompact(data.output_tax), color: '#dc2626' },
        { label: 'Input Tax (Recoverable)', value: formatAmountCompact(data.input_tax), color: '#16a34a' },
        { label: 'Net Payable', value: formatAmountCompact(data.net_payable), color: data.net_payable > 0 ? '#dc2626' : '#16a34a' },
        { label: 'Withholding Collected', value: formatAmountCompact(data.withholding_collected), color: '#9333ea' },
        { label: 'Active Jurisdictions', value: String(data.active_jurisdictions) },
        { label: 'Total Transactions', value: String(data.total_transactions) },
      ]} columns={3} />
    </div>
  );
}


/* -- Jurisdictions Tab -- */

function JurisdictionsTab() {
  const [rows, setRows] = useState<Jurisdiction[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<any>({ ...emptyJurForm });

  const load = useCallback(() => {
    authFetch(`${API}/jurisdictions`).then(r => r.json()).then(setRows).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...emptyJurForm }); setEditId(null); setShowModal(true); };
  const openEdit = (j: Jurisdiction) => {
    setForm({
      jurisdiction_code: j.jurisdiction_code,
      jurisdiction_name: j.jurisdiction_name,
      country: j.country,
      region: j.region || '',
      tax_type: j.tax_type,
      rate: String(j.rate),
      is_compound: j.is_compound,
      is_recoverable: j.is_recoverable,
      gl_account_payable: j.gl_account_payable || '',
      gl_account_receivable: j.gl_account_receivable || '',
      is_active: j.is_active,
    });
    setEditId(j.id);
    setShowModal(true);
  };

  const save = async () => {
    const body = { ...form, rate: parseFloat(form.rate) || 0 };
    const url = editId ? `${API}/jurisdictions/${editId}` : `${API}/jurisdictions`;
    const method = editId ? 'PUT' : 'POST';
    await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    setShowModal(false);
    load();
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const inp = (label: string, field: string, type = 'text') => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        type={type} value={form[field]}
        onChange={e => setForm({ ...form, [field]: e.target.value })}
        style={inputStyle}
      />
    </div>
  );

  const jurColumns: Column[] = [
    { key: 'jurisdiction_code', label: 'Code', render: (v: any) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'jurisdiction_name', label: 'Name' },
    { key: 'country', label: 'Country' },
    { key: 'region', label: 'Region', render: (v: any) => v || '-' },
    { key: 'tax_type', label: 'Type', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'rate', label: 'Rate', align: 'right', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{(v * 100).toFixed(2)}%</span> },
    { key: 'is_recoverable', label: 'Recoverable', render: (v: any) => v ? 'Yes' : 'No' },
    { key: 'gl_account_payable', label: 'GL Payable', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{v || '-'}</span> },
    { key: 'gl_account_receivable', label: 'GL Receivable', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{v || '-'}</span> },
    { key: 'is_active', label: 'Active', render: (v: any) => v ? 'Yes' : 'No' },
    { key: 'actions', label: '', render: (_: any, row: Jurisdiction) => <ActionButton label="Edit" onClick={() => openEdit(row)} variant="secondary" size="sm" /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: 'var(--text-hint)' }}>{rows.length} jurisdictions</span>
        <ActionButton label="+ Add Jurisdiction" onClick={openCreate} />
      </div>

      <DataTable columns={jurColumns} rows={rows} rowKey="id" emptyMessage="No jurisdictions configured" />

      {showModal && (
        <Modal open={showModal} title={editId ? 'Edit Jurisdiction' : 'Create Jurisdiction'} onClose={() => setShowModal(false)}>
          <div style={{ maxWidth: 480 }}>
            {!editId && inp('Jurisdiction Code', 'jurisdiction_code')}
            {inp('Jurisdiction Name', 'jurisdiction_name')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {inp('Country (ISO)', 'country')}
              {inp('Region', 'region')}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Tax Type</label>
              <select value={form.tax_type} onChange={e => setForm({ ...form, tax_type: e.target.value })}
                style={inputStyle}>
                {TAX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {inp('Rate (decimal, e.g. 0.0825)', 'rate', 'number')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {inp('GL Account Payable', 'gl_account_payable')}
              {inp('GL Account Receivable', 'gl_account_receivable')}
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_recoverable} onChange={e => setForm({ ...form, is_recoverable: e.target.checked })} />
                Recoverable
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_compound} onChange={e => setForm({ ...form, is_compound: e.target.checked })} />
                Compound
              </label>
              <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                Active
              </label>
            </div>
            <ActionButton label={editId ? 'Update' : 'Create'} onClick={save} />
          </div>
        </Modal>
      )}
    </div>
  );
}


/* -- Transactions Tab -- */

function TransactionsTab() {
  const { entity } = useGlobalEntity();
  const [rows, setRows] = useState<TaxTxn[]>([]);
  const [total, setTotal] = useState(0);
  const [period, setPeriod] = useState('');
  const [taxType, setTaxType] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (period) params.set('period', period);
    if (taxType) params.set('tax_type', taxType);
    params.set('limit', String(LIMIT));
    params.set('offset', String(offset));
    authFetch(withEntityParam(`${API}/transactions?${params}`, entity))
      .then(r => r.json())
      .then(d => { setRows(d.items || []); setTotal(d.total || 0); })
      .catch(() => {});
  }, [period, taxType, offset, entity]);

  useEffect(() => { load(); }, [load]);

  const txnColumns: Column[] = [
    { key: 'id', label: 'ID' },
    { key: 'tax_period', label: 'Period' },
    { key: 'entity_code', label: 'Entity', render: (v: any) => v || '-' },
    { key: 'tax_type', label: 'Type', render: (v: any) => <StatusBadge status={v} /> },
    { key: 'transaction_type', label: 'Txn Type' },
    { key: 'transaction_id', label: 'Txn ID' },
    { key: 'base_amount', label: 'Base Amount', align: 'right', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{formatAmount(v)}</span> },
    { key: 'tax_rate', label: 'Rate', align: 'right', render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{(v * 100).toFixed(2)}%</span> },
    { key: 'tax_amount', label: 'Tax Amount', align: 'right', render: (v: any) => <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{formatAmount(v)}</span> },
    { key: 'is_recoverable', label: 'Recoverable', render: (v: any) => v ? 'Yes' : '-' },
    { key: 'status', label: 'Status', render: (v: any) => <StatusBadge status={v} /> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterBar filters={[
          { key: 'period', label: 'Period', type: 'search', value: period, onChange: v => { setPeriod(v); setOffset(0); }, width: 150, placeholder: 'Period (YYYY-MM)' },
          {
            key: 'taxType', label: 'Type', type: 'select', value: taxType,
            onChange: v => { setTaxType(v); setOffset(0); },
            options: [
              { value: 'SALES_TAX', label: 'Sales Tax' },
              { value: 'VAT_OUTPUT', label: 'VAT Output' },
              { value: 'VAT_INPUT', label: 'VAT Input' },
              { value: 'WITHHOLDING', label: 'Withholding' },
            ],
          },
        ]} />
        <span style={{ fontSize: 13, color: 'var(--text-hint)' }}>{total} transactions</span>
      </div>

      <DataTable columns={txnColumns} rows={rows} rowKey="id" emptyMessage="No tax transactions" />

      {total > LIMIT && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
          <ActionButton label="Prev" onClick={() => setOffset(Math.max(0, offset - LIMIT))} variant="secondary" size="sm" disabled={offset === 0} />
          <span style={{ fontSize: 12, alignSelf: 'center', color: 'var(--text-hint)' }}>
            {offset + 1}-{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <ActionButton label="Next" onClick={() => setOffset(offset + LIMIT)} variant="secondary" size="sm" disabled={offset + LIMIT >= total} />
        </div>
      )}
    </div>
  );
}


/* -- VAT Return Tab -- */

function VATReturnTab() {
  const entities = useEntities();
  const { entity: globalEntity } = useGlobalEntity();
  const [entity, setEntity] = useState(globalEntity || '');
  useEffect(() => { if (globalEntity) setEntity(globalEntity); }, [globalEntity]);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState<VATReturn | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!entity || !period) return;
    setLoading(true);
    authFetch(`${API}/vat-return?period=${period}&entity_code=${entity}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entity, period]);

  const inputStyle: React.CSSProperties = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13, background: 'var(--bg-card)', color: 'var(--text-primary)' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Entity</label>
          <select value={entity} onChange={e => setEntity(e.target.value)}
            style={{ ...inputStyle, minWidth: 180 }}>
            <option value="">Select entity...</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} - {e.entity_name}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Period</label>
          <input type="text" value={period} onChange={e => setPeriod(e.target.value)} placeholder="YYYY-MM"
            style={{ ...inputStyle, width: 120 }} />
        </div>
        <ActionButton label="Generate" onClick={load} disabled={!entity || !period} />
      </div>

      {loading && <p style={{ color: 'var(--text-hint)' }}>Loading...</p>}

      {data && !loading && (
        <div style={{
          border: '1px solid var(--border-subtle)', borderRadius: 12,
          padding: 24, background: 'var(--bg-card)', maxWidth: 500,
        }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--navy-900)' }}>
            VAT Return: {data.entity_code} / {data.period}
          </h3>

          <KpiGrid items={[
            { label: `Output VAT (${data.output_count} txns)`, value: formatAmountCompact(data.output_vat), color: '#dc2626' },
            { label: `Input VAT (${data.input_count} txns)`, value: formatAmountCompact(data.input_vat), color: '#16a34a' },
          ]} columns={2} />

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-hint)', marginBottom: 4 }}>Recoverable Input</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>{formatAmount(data.recoverable_input)}</div>

            <div style={{
              padding: 16, borderRadius: 8,
              background: data.net_payable > 0 ? '#fef2f2' : '#f0fdf4',
              border: `1px solid ${data.net_payable > 0 ? '#fecaca' : '#bbf7d0'}`,
            }}>
              <div style={{ fontSize: 12, color: 'var(--text-hint)' }}>
                {data.net_payable > 0 ? 'Net Payable' : 'Net Refundable'}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: data.net_payable > 0 ? '#dc2626' : '#16a34a' }}>
                {formatAmount(data.net_payable > 0 ? data.net_payable : data.net_refundable)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
